import { useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import SpaceBetween from '@cloudscape-design/components/space-between';
import BreadcrumbGroup from '@cloudscape-design/components/breadcrumb-group';
import Spinner from '@cloudscape-design/components/spinner';
import Button from '@cloudscape-design/components/button';
import { useAppSelector, useAppDispatch } from 'src/store/hooks';
import { fetchFiles, clearFile } from 'src/store/slices/file/fileSlice';
import { addNotification, clearNotifications } from 'src/store/slices/notifications/notificationSlice';
import { FileDetailsPanel } from './FileDetailsPanel';

const DETAIL_PAGE_SIZE = 100;

export const FileDetailPage = () => {
  const [searchParams] = useSearchParams();
  const name = searchParams.get('name') ?? '';
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { data, loading, error } = useAppSelector(state => state.file);

  useEffect(() => {
    if (name) {
      dispatch(clearNotifications());
      dispatch(clearFile());
      dispatch(fetchFiles({ fileNames: [name], pageNumber: 1, pageSize: DETAIL_PAGE_SIZE }));
      return () => { dispatch(clearFile()); };
    }
    return () => { dispatch(clearFile()); };
  }, [name, dispatch]);

  useEffect(() => {
    if (error) {
      dispatch(clearNotifications());
      dispatch(addNotification({ type: 'error', header: 'Failed to load file', content: error }));
    }
  }, [error, dispatch]);

  const handleRefresh = () => {
    if (name) {
      dispatch(clearFile());
      dispatch(fetchFiles({ fileNames: [name], pageNumber: 1, pageSize: DETAIL_PAGE_SIZE }));
    }
  };

  const executions = data?.executions ?? [];
  const notFound = (data?.notFound ?? []).includes(name);

  return (
    <Box padding={{ horizontal: 'l', top: 'l', bottom: 'xl' }}>
      <SpaceBetween size="l">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <BreadcrumbGroup
            items={[
              { text: 'Search', href: '/' },
              { text: `File: ${name}`, href: '#' },
            ]}
            onFollow={(e) => {
              e.preventDefault();
              navigate(e.detail.href);
            }}
          />
          <Button iconName="refresh" onClick={handleRefresh} loading={loading}>
            Refresh
          </Button>
        </div>

        {loading && !data && (
          <Box textAlign="center" padding={{ top: 'xxxl' }}>
            <SpaceBetween size="s" alignItems="center">
              <Spinner size="large" />
              <Box variant="p" color="text-body-secondary">
                Loading file {name}...
              </Box>
            </SpaceBetween>
          </Box>
        )}

        {notFound && (
          <Alert type="warning" header="File not found">
            No executions matched the file name {name}.
          </Alert>
        )}

        {executions.length > 0 && (
          <SpaceBetween size="l">
            {executions.map((execution) => (
              <FileDetailsPanel key={execution.id} execution={execution} />
            ))}
          </SpaceBetween>
        )}
      </SpaceBetween>
    </Box>
  );
};
